import React, { useMemo, useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import InfinityCard from "../../components/InfinityCard";
import shieldIcon from "../../@shared/assets/icons/sheild.png";
import heartIcon from "../../@shared/assets/icons/heart.png";
import crosshairIcon from "../../@shared/assets/icons/cross-hair.png";
import swordIcon from "../../@shared/assets/icons/swords_icon2.png";

const CurrentPlayer = ({
  name,
  character,
  hand,
  selectCard,
  setSelectedPlayer,
  isCurrentPlayer,
  playerTurn,
  updatePlayerStance,
  setPlayerMoveType,
  handDisabled,
  finishedDrawAnimation,
  attackAnimation,
  isTutorial,
  progress,
  advanceStep,
}) => {
  const cardRefs = useMemo(() => {
    return hand?.map(() => React.createRef());
  }, [hand]);

  const [handReady, setHandReady] = useState(false);

  useEffect(() => {
    console.log("🌀 finishedDrawAnimation changed:", finishedDrawAnimation);
    if (finishedDrawAnimation) {
      setHandReady(true);
    }
  }, [finishedDrawAnimation]);

  const totalHealth =
    Number(character?.health) +
    Number(
      character?.buffs?.health?.reduce((acc, buff) => acc + buff.amt, 0) || 0
    );
  const totalAttack =
    Number(character?.attack) +
    Number(
      character?.buffs?.attack?.reduce((acc, buff) => acc + buff.amt, 0) || 0
    );
  const totalDefense =
    Number(character?.defense) +
    Number(
      character?.buffs?.defense?.reduce((acc, buff) => acc + buff.amt, 0) || 0
    );
  const totalAccuracy =
    Number(character?.accuracy) +
    Number(
      character?.buffs?.accuracy?.reduce((acc, buff) => acc + buff.amt, 0) || 0
    );

  const renderStatIcon = (icon, value, style = {}) => (
    <Box style={{ ...styles.stat, ...style }}>
      <Box style={styles.relativeContainer}>
        <img src={icon} alt="" style={styles.statIcon} />
        <Typography style={styles.statText}>{value}</Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {isCurrentPlayer ? (
        <Box style={styles.currentPlayerContainer}>
          <Box style={styles.relativeContainer}>
            <img
              src={character.imageUrl}
              alt="player"
              style={styles.playerImage}
            />

            {/* Hand Cards */}
            {!attackAnimation && (
              <Box style={styles.handContainer}>
                {hand.map((card, index) => {
                  const cardRef = cardRefs[index];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 50 }}
                      animate={handReady ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: index * 0.15, duration: 0.4 }}
                      style={{
                        ...styles.cardContainer,
                        right:
                          window.innerWidth >= 900 ? index * 130 : index * 45,
                        top: window.innerWidth >= 900 ? index * 10 : index * 7,
                        zIndex: 100 - (index - hand.length),
                      }}
                    >
                      <Box
                        ref={cardRef}
                        style={{ height: window.innerWidth >= 900 ? 250 : 130 }}
                      >
                        <InfinityCard
                          {...card}
                          heightPer={100}
                          containerHeight={
                            window.innerWidth > 900
                              ? window.innerHeight * 0.2
                              : window.innerHeight * 0.2
                          }
                          borderPx={4}
                          disabled={handDisabled}
                          onClick={() => {
                            const rect =
                              cardRef.current?.getBoundingClientRect?.();
                            if (rect) {
                              if (isTutorial) {
                                if (
                                  card.name === "Fortify Elixir" &&
                                  progress?.currentStepIndex === 4
                                ) {
                                  const { x, y, width, height } = rect;
                                  selectCard(card, { x, y, width, height });
                                  setSelectedPlayer(null);
                                  advanceStep();
                                }

                                if (
                                  card.areaAffected.includes("Accuracy") &&
                                  progress?.currentStepIndex === 9
                                ) {
                                  const { x, y, width, height } = rect;
                                  selectCard(card, { x, y, width, height });
                                  setSelectedPlayer(null);
                                }
                              } else {
                                const { x, y, width, height } = rect;
                                selectCard(card, { x, y, width, height });
                                setSelectedPlayer(null);
                              }
                            }
                          }}
                        />
                      </Box>
                    </motion.div>
                  );
                })}
              </Box>
            )}

            {/* Stats Panel */}
            <Box style={styles.playerStatsContainer}>
              <Box style={styles.relativeContainer}>
                <Box style={styles.healthBarContainer}>
                  <Box
                    style={{
                      ...styles.healthBar,
                      backgroundColor: "#fbcf57",
                      width: `${
                        totalDefense > character.startingHealth
                          ? 100
                          : (totalDefense / character.startingHealth) * 100
                      }%`,
                      top: 10,
                      boxShadow: "3px 3px 10px #00000060",
                    }}
                  />
                  <Box
                    style={{
                      ...styles.healthBar,
                      width: `${
                        totalHealth > character.startingHealth
                          ? 100
                          : (totalHealth / character.startingHealth) * 100
                      }%`,
                      boxShadow: "3px 3px 10px #00000090",
                    }}
                  />
                </Box>

                {renderStatIcon(shieldIcon, totalDefense, {
                  top: window.innerWidth >= 900 ? 15 : 15,
                  left: window.innerWidth >= 900 ? "21%" : "18%",
                })}
                {renderStatIcon(heartIcon, totalHealth, {
                  top: window.innerWidth >= 900 ? -2 : 0,
                  left: window.innerWidth >= 900 ? "23%" : "23%",
                })}
                {renderStatIcon(crosshairIcon, totalAccuracy, {
                  top: window.innerWidth >= 900 ? "15%" : "17%",
                  left: window.innerWidth >= 900 ? "49%" : "51%",
                })}
                {renderStatIcon(swordIcon, totalAttack, {
                  top: window.innerWidth >= 900 ? "17%" : "17%",
                  left: window.innerWidth >= 900 ? "46%" : "44%",
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box style={styles.selectedPlayerContainer}>
          <Box
            style={{
              ...styles.relativeContainer,
              containerType: "inline-size",
            }}
          >
            <Typography
              style={{
                position: "absolute",
                top: "20dvh",
                left: "7%",
                transform: "rotate(-8deg)",
                zIndex: 2000,
                fontWeight: 900,
                fontSize: "3cqw",
                color: "#fff",
                textShadow: ` -2px -2px 0 #000, 2px -2px 0 #000, -2px  2px 0 #000, 2px  2px 0 #000`,
              }}
            >
              {character.name.toUpperCase()}
            </Typography>

            <Typography
              style={{
                position: "absolute",
                top: "22dvh",
                left: "5%",
                transform: "rotate(-8deg)",
                zIndex: 2000,
                fontWeight: 700,
                fontSize: "1.8cqw",
                color: "#fff",
                textShadow: ` -2px -2px 0 #000, 2px -2px 0 #000, -2px  2px 0 #000, 2px  2px 0 #000`,
              }}
            >
              @{name.toUpperCase()}
            </Typography>
            <Box
              style={{
                position: "absolute",
                // bottom: -10,

                left: window.innerWidth >= 900 ? -25 : -20,
                // height: window.innerWidth >= 900 ? 175 : 150,
                // width: "180%",
                width: window.innerWidth >= 900 ? "200%" : "275%",
                backgroundColor: "#16116e",
                border: "4px solid #b90c98",
                transform: "rotate(-8deg)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                height: 200,
                bottom: window.innerWidth >= 900 ? "50%" : "50%",
              }}
            >
              <img
                src={character.imageUrl}
                alt="Selected Player"
                style={{
                  ...styles.playerImage,
                  width: 200,
                  height: 200,
                  left: -20,
                  top: "15%",
                }}
              />

              <Box style={styles.relativeContainer}>
                <Box
                  style={{
                    ...styles.playerStats,
                    bottom: "-14%",
                    left: "16%",
                    width: "46%",
                  }}
                >
                  <Box style={styles.relativeContainer}>
                    {/* Health Bars */}
                    <Box
                      style={{
                        ...styles.healthBarContainer,
                        left: window.innerWidth >= 900 ? -220 : -70,
                        top: window.innerWidth >= 900 ? 40 : 30,
                        width: window.innerWidth >= 900 ? "48%" : "38%",
                      }}
                    >
                      <Box
                        style={{
                          ...styles.healthBar,
                          width: `${
                            totalHealth > character.startingHealth
                              ? 100
                              : (totalHealth / character.startingHealth) * 100
                          }%`,
                        }}
                      />
                      <Box
                        style={{
                          ...styles.healthBar,
                          width: `${
                            totalDefense > character.startingHealth
                              ? 100
                              : (totalDefense / character.startingHealth) * 100
                          }%`,
                          backgroundColor: "#fbcf57",
                          top: 10,
                        }}
                      />
                    </Box>

                    {/* Defense */}
                    <Box
                      style={{
                        ...styles.stat,
                        top: window.innerWidth >= 900 ? 35 : 25,
                        left: window.innerWidth >= 900 ? -260 : -110,
                      }}
                    >
                      <Box style={styles.relativeContainer}>
                        <img
                          src={shieldIcon}
                          alt="defense"
                          style={styles.statIcon}
                        />
                        <Typography style={styles.statText}>
                          {totalDefense}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Health */}
                    <Box
                      style={{
                        ...styles.stat,
                        top: window.innerWidth >= 900 ? 15 : 0,
                        left: window.innerWidth >= 900 ? -235 : -85,
                      }}
                    >
                      <Box style={styles.relativeContainer}>
                        <img
                          src={heartIcon}
                          alt="health"
                          style={styles.statIcon}
                        />
                        <Typography style={styles.statText}>
                          {totalHealth}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Accuracy */}
                    <Box
                      style={{
                        ...styles.stat,
                        left: window.innerWidth >= 900 ? "8%" : "10%",
                        top: window.innerWidth >= 900 ? 45 : 40,
                      }}
                    >
                      <Box style={styles.relativeContainer}>
                        <img
                          src={crosshairIcon}
                          alt="accuracy"
                          style={styles.statIcon}
                        />
                        <Typography style={styles.statText}>
                          {totalAccuracy}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Attack */}
                    <Box
                      style={{
                        ...styles.stat,
                        top: window.innerWidth >= 900 ? 45 : 40,
                        left: window.innerWidth >= 900 ? "2%" : "0%",
                      }}
                    >
                      <Box style={styles.relativeContainer}>
                        <img
                          src={swordIcon}
                          alt="attack"
                          style={styles.statIcon}
                        />
                        <Typography style={styles.statText}>
                          {totalAttack}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

const styles = {
  currentPlayerContainer: {
    position: "absolute",
    bottom: "-2%",
    left: "-1%",
    width: "100%",
    height: "23%",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: 10,
  },
  selectedPlayerContainer: {
    width: "100%",
    height: "100%",
    marginTop: "-7%",
  },
  relativeContainer: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  playerImage: {
    width: window.innerWidth >= 900 ? "40%" : "55%",
    height: window.innerWidth >= 900 ? "150%" : "100%",
    borderRadius: 50,
    position: "absolute",
    top: window.innerWidth >= 900 ? "-90%" : -70,
    left: window.innerWidth >= 900 ? "-3%" : -40,
    zIndex: 100,
    objectFit: "contain",
  },
  handContainer: {
    position: "absolute",

    top: window.innerWidth >= 900 ? -160 : -100,
    right: -50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardContainer: {
    position: "absolute",
    zIndex: 100,
  },
  playerStatsContainer: {
    position: "absolute",
    bottom: -10,
    left: -20,

    height: window.innerWidth >= 900 ? "100%" : 150,
    width: "180%",
    backgroundColor: "#16116e",
    border: "4px solid #b90c98",
    transform: "rotate(-8deg)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  healthBarContainer: {
    position: "absolute",
    top: window.innerWidth >= 900 ? "10%" : "13%",
    left: window.innerWidth >= 900 ? "25%" : "28%",
    width: window.innerWidth >= 900 ? "32%" : "30%",
    height: 12,
    backgroundColor: "#00000050",
    borderRadius: 10,
  },
  healthBar: {
    position: "absolute",
    top: -10,
    left: 0,
    height: "100%",
    backgroundColor: "#c3090c",
    borderRadius: 10,
  },
  stat: {
    position: "absolute",
    height: 50,
    width: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statIcon: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: "50%",
    left: "50%",

    transform: "translate(-50%, -50%)",
  },
  statText: {
    color: "#000",
    fontSize: 35,

    fontWeight: "bold",
    textAlign: "center",
    textShadow: "2px 2px #fff",
    position: "relative",
    zIndex: 2,
  },
  toggleStanceContainer: {
    position: "absolute",
    // top: "10%",
    top: window.innerWidth >= 900 ? "-150%" : "-35%",
    left: window.innerWidth >= 900 ? "7%" : 5,
    width: window.innerWidth >= 900 ? 115 : 100,
    height: window.innerWidth >= 900 ? 115 : 100,

    zIndex: 1000,
  },
  toggleStanceButton: {
    width: "100%",
    height: "100%",
    position: "relative",
    top: "110%",
    left: "-10%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // transform: "rotate(8deg)",
    cursor: "pointer",
  },
  stanceText: {
    color: "#000",

    fontSize: window.innerWidth >= 900 ? 10 : 10,
    fontWeight: window.innerWidth >= 900 ? 900 : 700,
    textAlign: "center",
    borderRadius: 4,
    border: "3px solid #000",
    padding: 2,
    zIndex: 101,
    backgroundColor: "#fff",
    textShadow: "2px 2px #000",
  },
  toggleIcon: {
    height: window.innerWidth >= 900 ? "60%" : "90%",
    width: window.innerWidth >= 900 ? "60%" : "90%",
    position: "absolute",

    top: window.innerWidth >= 900 ? "53%" : "50%",
    left: window.innerWidth >= 900 ? "53%" : "55%",
    transform: "translate(-50%, -50%)",
  },
};

export default CurrentPlayer;
